import axios from "../utils/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { normalizeListDurationForQuery } from "../utils/listTimePeriod";

const normalizeTimeframe = (timeframe) => {
  const value = String(timeframe || "").toLowerCase();
  const map = {
    today: "today",
    daily: "today",
    day: "today",
    weekly: "week",
    week: "week",
    monthly: "month",
    month: "month",
    yearly: "year",
    year: "year",
    // Offer chart internal filter values
    thismonth: "thismonth",
    lastmonth: "lastmonth",
    lastweek: "lastweek",
  };
  return map[value] || value;
};

export const getUsers = createAsyncThunk("users", async (payload, thunkAPI) => {
  try {
    const queryParams = new URLSearchParams();

    // Add all available parameters to the query string
    if (payload?.page) queryParams.append("page", payload.page);
    if (payload?.limit) queryParams.append("limit", payload.limit);
    if (payload?.search) queryParams.append("search", payload.search);
    if (payload?.role) queryParams.append("role", payload.role);
    if (payload?.status || payload?.memberStatus) queryParams.append("status", payload?.status || payload?.memberStatus);
    if (payload?.subscriptionStatus || payload?.subStatus) queryParams.append("subscriptionStatus", payload?.subscriptionStatus || payload?.subStatus);
    if (payload?.merchantId || payload?.merchant) queryParams.append("merchantId", payload?.merchantId || payload?.merchant);
    if (payload?.fundraiserId || payload?.fundraiser) queryParams.append("fundraiserId", payload?.fundraiserId || payload?.fundraiser);
    const timeValue = normalizeListDurationForQuery(payload?.duration || payload?.timePeriod || payload?.dateFilter);
    if (timeValue) queryParams.append("duration", timeValue);
    if (payload?.startDate) queryParams.append("startDate", payload.startDate);
    if (payload?.endDate) queryParams.append("endDate", payload.endDate);

    // Build the final URL with all query parameters
    const queryString = queryParams.toString();
    const url = `admin/users?${queryString}`;
    const response = await axios.get(url);
    return response?.data;
  } catch (error) {
    return thunkAPI?.rejectWithValue(error?.response?.data || error);
  }
});

export const getTransactions = createAsyncThunk(
  "admin/transactions",
  async (filter = "") => {
    try {
      const response = await axios.get(`admin/transactions?filter=${filter}`);
      return response?.data;
    } catch (error) {
      return error;
    }
  }
);

export const getUserById = createAsyncThunk(
  "admin/users",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.get(`admin/users/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// Admin Dashboard Stats
export const getAdminFundStats = createAsyncThunk(
  "admin/fundStats",
  async (payload = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (payload?.timeframe) params.append("timeframe", normalizeTimeframe(payload.timeframe));
      // cache buster to avoid 304 when data just changed
      params.append("_", Date.now().toString());
      const url = `admin/fundStats?${params.toString()}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getAdminFundBreakdown = createAsyncThunk(
  "admin/fundbreakdown",
  async (payload = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (payload?.timeframe) params.append("timeframe", normalizeTimeframe(payload.timeframe));
      params.append("_", Date.now().toString());
      const url = `admin/fundbreakdown?${params.toString()}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getAdminOfferStats = createAsyncThunk(
  "admin/offerStats",
  async (payload = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (payload?.timeframe) params.append("timeframe", normalizeTimeframe(payload.timeframe));
      // optional: scope by merchant/fundraiser if backend supports it in future
      if (payload?.merchantId) params.append("merchantId", payload.merchantId);
      const qs = params.toString();
      const url = qs ? `admin/offerStats?${qs}` : "admin/offerStats";

      console.log("getAdminOfferStats - GET", url);
      const response = await axios.get(url);
      console.log("getAdminOfferStats - API response:", response?.data);
      return response?.data;
    } catch (error) {
      console.error("getAdminOfferStats - API error:", error);
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// Active/Inactive status summaries
export const getMerchantStatusSummary = createAsyncThunk(
  "admin/merchantStatus",
  async (payload = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (payload?.timeframe) params.append("timeframe", normalizeTimeframe(payload.timeframe));
      if (payload?.year) params.append("year", String(payload.year));
      if (payload?.currentYear) params.append("currentYear", String(payload.currentYear));
      params.append("_", Date.now().toString());
      const qs = params.toString();
      const response = await axios.get(qs ? `admin/merchantStatus?${qs}` : "admin/merchantStatus");
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getFundraiserStatusSummary = createAsyncThunk(
  "admin/fundraiserStatus",
  async (payload = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (payload?.timeframe) params.append("timeframe", normalizeTimeframe(payload.timeframe));
      if (payload?.year) params.append("year", String(payload.year));
      if (payload?.currentYear) params.append("currentYear", String(payload.currentYear));
      params.append("_", Date.now().toString());
      const qs = params.toString();
      const response = await axios.get(qs ? `admin/fundraiserStatus?${qs}` : "admin/fundraiserStatus");
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

// Delete Member
export const deleteUser = createAsyncThunk(
  "admin/deleteMember",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.delete(`admin/member/${payload?.id}`, {
        data: {
          status: payload?.status || "deleted",
        },
      });
      return response?.data;
    } catch (error) {
       return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// Delete Merchant
export const deleteMerchantAdmin = createAsyncThunk(
  "admin/deleteMerchant",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.delete(`admin/merchant/${payload?.id}`);
      return response?.data;
    } catch (error) {
       return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// Delete Fundraiser
export const deleteFundraiserAdmin = createAsyncThunk(
  "admin/deleteFundraiser",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.delete(`admin/fundraiser/${payload?.id}`);
      return response?.data;
    } catch (error) {
       return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);

// Update Member Status
export const updateUserStatus = createAsyncThunk(
  "admin/updateUserStatus",
  async (payload, thunkAPI) => {
    try {
      // payload: { id, status: 'live' | 'disabled' | 'deleted' }
      // The backend member route uses DELETE with a status body.
      // Keep the UI wording as "Live" and send the backend status directly.
      const nextStatus = payload?.status;
      const response = await axios.delete(`admin/member/${payload?.id}`, {
        data: {
          status: nextStatus,
          reason: payload?.reason,
        },
      });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error?.response?.data || error);
    }
  }
);
